import {useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import {useTranslations} from 'use-intl'
import {Image} from '~/gql/graphql'

type ImageProps = {
    className?: string
    imageClassName?: string
    image: Image
    lazy?: boolean
    sizes?: string
}

const ImageComposition = ({className, image, imageClassName, lazy = true, sizes}: ImageProps) => {
    const t = useTranslations('imageComposition')
    const imgRef = useRef<HTMLImageElement>(null)
    const [loading, setLoading] = useState<boolean>(true)

    useEffect(() => {
        const isCompleted = imgRef.current?.complete ?? false
        setLoading(!isCompleted)
    }, [image?.src, image?.srcset])

    return (
        <div className={clsx('relative aspect-square', className)}>
            <div
                className="absolute inset-0 z-10 size-0 opacity-0 data-[state=visible]:size-full data-[state=visible]:opacity-100 delay-500 transition-opacity"
                data-state={loading ? 'visible' : 'hidden'}
                aria-hidden={!loading}
            >
                <div className="size-full bg-gray-800/80 animate-pulse" />
                <div className="sr-only">{t('loading')}</div>
            </div>
            <img
                ref={imgRef}
                className={clsx(
                    'size-full object-center object-contain',
                    loading && 'opacity-0',
                    imageClassName
                )}
                src={image?.src ?? ''}
                srcSet={image?.srcset ?? ''}
                sizes={sizes}
                alt={image?.altText ?? ''}
                loading={lazy ? 'lazy' : 'eager'}
                onLoad={() => {
                    setLoading(false)
                }}
                onError={() => {
                    setLoading(false)
                }}
            />
        </div>
    )
}

export default ImageComposition
